import {
  EMAIL_ATTACHMENT_MAX_BYTES,
  MessagingPlatform,
} from '@this+that/models';

// Margin by which a server row's createdAt must EXCEED the local save
// timestamp to count as a genuine external (Gmail web) edit. The backend's
// own echo of a save is always a little OLDER than the local commit that
// triggered it (its createdAt is the API Lambda's own clock when it called
// Gmail, which happens after the local save), so anything within this
// margin is our own echo, not confirmation.
export const CONFIRMATION_CLOCK_SKEW_MS = 30_000;

// After this long since the last local save with no confirming external
// edit, the server becomes the truth again regardless of freshness: a
// present server row wins outright, an absent one drops the local row. Also
// what eventually clears a draft deleted in Gmail web, since a delete there
// never produces a confirming row.
export const UNCONFIRMED_DRAFT_TTL_MS = 15 * 60_000;

export const COMPOSE_SNAPSHOT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const COMPOSE_SNAPSHOT_MAX_PER_USER = 20;

// Real (decoded) bytes, matching Gmail's own 25 MB message limit.
export const COMPOSE_SNAPSHOT_MAX_ATTACHMENT_BYTES = EMAIL_ATTACHMENT_MAX_BYTES;

export const BASE64_PADDING_CHAR = '=';

export const BASE64_MAX_PADDING = 2;

export const BASE64_CHARS_PER_GROUP = 4;

export const BASE64_BYTES_PER_GROUP = 3;

export const COMPOSE_SNAPSHOT_WRITE_DEBOUNCE_MS = 1000;

export const COMPOSE_SNAPSHOT_EXCERPT_MAX_CHARS = 120;

export const COMPOSE_SNAPSHOT_INDEX_MAX_RECIPIENTS = 3;

export const COMPOSE_SNAPSHOT_ORPHAN_GRACE_MS = 60_000;

// Autosave/snapshot triggers that cascade out of a restore revert must not re-record it.
export const COMPOSE_SNAPSHOT_SETTLE_WINDOW_MS = 750;

export const COMPOSE_RECOVERY_TOAST_DURATION_MS = 30 * 60_000;

export const COMPOSE_REVERT_TOAST_DURATION_MS = 8000;

export const COMPOSE_KEPT_LOCALLY_TOAST_DURATION_MS = 10_000;

// Backoff after a failed draft save; the last entry repeats until a save succeeds.
export const DRAFT_AUTO_RETRY_DELAYS_MS = [
  10_000, 30_000, 60_000, 120_000, 300_000,
] as const;

export const HTTP_STATUS_PAYLOAD_TOO_LARGE = 413;

// Lambda's synchronous invoke limit (6 MiB) minus room for the API Gateway event envelope and headers.
export const MAX_EMAIL_REQUEST_BYTES = 6 * 1024 * 1024 - 64 * 1024;

export const COMPOSE_SNAPSHOT_SYNC_LOCK_NAME = 'compose-snapshot-sync';

// Attempts per session id in one app session before the sync waits for the next online/visibility trigger.
export const COMPOSE_SNAPSHOT_SYNC_MAX_ATTEMPTS = 5;

// Longer than the 60 s Lambda timeout, so a create that timed out has finished server-side before it is repeated.
export const COMPOSE_SNAPSHOT_SYNC_CREATE_TIMEOUT_COOLDOWN_MS = 2 * 60_000;

export const MISSING_SNAPSHOT_TOAST_DURATION_MS = 5000;

export const COMPOSE_SNAPSHOT_REHYDRATE_COALESCE_MS = 150;

export const COMPOSE_SNAPSHOT_SWEEP_THROTTLE_MS = 5 * 60_000;

export const COMPOSE_SNAPSHOT_SWEEP_MAX_PROBES = 5;

// Inline attachments ride in the JSON as base64 (4 chars per 3 bytes) under the request cap.
export const INLINE_EMAIL_ATTACHMENT_MAX_BYTES = Math.floor(
  (MAX_EMAIL_REQUEST_BYTES * BASE64_BYTES_PER_GROUP) / BASE64_CHARS_PER_GROUP,
);

// Lifecycle expires after 1 day (run at the next UTC midnight, never before 24 h); 20 h keeps a long-open compose from sending a reaped key.
export const EMAIL_ATTACHMENT_REUSE_WINDOW_MS = 20 * 60 * 60 * 1000;

export const EMAIL_ATTACHMENT_UPLOAD_URL_PATH =
  '/emails/attachments/upload-url';

// Never a Gmail message id or an Outlook changeKey; relies on the backend checking the version before any write.
export const DRAFT_VERSION_PROBE = '__draft-version-probe__';

// The webhook that writes the edited-in-provider row can lag the open; delays double: 1 s, 2 s, 4 s.
export const STALE_DRAFT_RECOVERY_MAX_FETCH_ATTEMPTS = 4;

export const STALE_DRAFT_RECOVERY_BASE_DELAY_MS = 1000;

export const DRAFT_SAVING_BADGE_DELAY_MS = 400;

export const DRAFT_SAVING_BADGE_MIN_VISIBLE_MS = 600;

// Providers whose /emails/send path reads attachments from S3 instead of inline base64.
export const S3_ATTACHMENT_PLATFORMS: ReadonlySet<MessagingPlatform> = new Set([
  MessagingPlatform.GMAIL,
  MessagingPlatform.OUTLOOK,
]);
